import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import {
  cardOutline,
  fonts,
  headingFonts,
  mainButtonStyles,
  selectStyles,
  textFieldStyles,
} from "../../../../Styles/Styles";

export default function AddService() {
  const [service, setService] = useState({
    name: "",
    description: "",
    image: null,
    imagePreview: null,
    parent: "",
  });
  const [loading, setLoading] = useState(false);
  const [parentServices, setParentServices] = useState([]); // List of parent services
  const [error, setError] = useState("");

  useEffect(() => {
    // Fetch available parent services
    const fetchParentServices = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/services/parentServices`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch categories.");
        }

        const data = await response.json();
        setParentServices(data); // Assume response is { categories: [...] }
      } catch (error) {
        console.error("Error fetching categories:", error);
        setError("Failed to load categories. Please try again.");
      }
    };

    fetchParentServices();
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const preview = file ? URL.createObjectURL(file) : null;

    setService({
      ...service,
      image: file,
      imagePreview: preview,
    });
  };

  const handleSaveService = async () => {
    setLoading(true);
    if (!service.name.trim() || !service.description) {
      setError("Service name and description are required.");
      return;
    }

    const formData = new FormData();
    formData.append("name", service.name.trim());
    formData.append("description", service.description.trim());
    formData.append("parent", service.parent);
    if (service.image) {
      formData.append("image", service.image);
    }
    // formData.append("parent", service.parent);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/services/addnew`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      alert("Service added successfully!");
      setService({
        name: "",
        description: "",
        image: null,
        imagePreview: null,
        parent: "",
      });
      setError("");
    } catch (error) {
      console.error("Error saving service:", error);
      setError("Failed to save service. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 5 }}>
      <Paper sx={{ padding: 3, ...cardOutline }}>
        <Typography
          variant="h6"
          component="h1"
          gutterBottom
          sx={{ ...headingFonts }}
        >
          Add New Service
        </Typography>
        {error && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          label="Service Name"
          variant="outlined"
          margin="normal"
          fullWidth
          value={service.name}
          onChange={(e) => setService({ ...service, name: e.target.value })}
          sx={{ ...textFieldStyles }}
        />
        <TextField
          label="Service Description"
          variant="outlined"
          margin="normal"
          fullWidth
          multiline
          minRows={2}
          value={service.description}
          onChange={(e) =>
            setService({ ...service, description: e.target.value })
          }
          sx={{ ...textFieldStyles }}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel sx={{ ...fonts }} id="parent-service-label">
            Parent Service
          </InputLabel>
          <Select
            label="Parent Service"
            labelId="parent-service-label"
            value={service.parent}
            onChange={(e) => setService({ ...service, parent: e.target.value })}
            sx={{ ...selectStyles }}
          >
            <MenuItem value="" sx={{ ...fonts }}>
              <em>None</em>
            </MenuItem>
            {parentServices.map((service) => (
              <MenuItem key={service._id} value={service._id} sx={{ ...fonts }}>
                {service.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          accept="image/*"
          onChange={handleFileChange}
          label="Service Image"
          type="file"
          variant="outlined"
          fullWidth
          required
          margin="normal"
          InputLabelProps={{
            shrink: true,
          }}
          sx={{ ...textFieldStyles }}
        />
        {service.imagePreview && (
          <Box sx={{ marginTop: 2 }}>
            <img
              src={service.imagePreview}
              alt="Service Preview"
              style={{ width: "200px" }}
            />
          </Box>
        )}

        <Button
          onClick={handleSaveService}
          className="main-button-style"
          sx={{ mt: 2, ...mainButtonStyles }}
          disabled={loading}
        >
          {loading ? (
            <>
              <span>Saving...</span>
              <CircularProgress size={20} sx={{ color: "white" }} />
            </>
          ) : (
            <>
              <span>Save Service</span>
              <ArrowForwardOutlinedIcon fontSize="small" />
            </>
          )}
        </Button>
      </Paper>
    </Box>
  );
}
